import { AxiosError, AxiosRequestConfig } from 'axios';
import { apiDelete, apiGet, apiPost, apiPut } from 'services/api';
import {
  EQuoteStatus,
  IOutputCountsByStatusResponse,
  IOutputQuoteListFilters,
  IQuoteApiResponse,
  IQuoteListByStatusResponse,
  IQuoteResponsibleUser
} from 'types/quote.types';
import { IQuotePurchaseTrackingOrder } from 'types/quoteDetails.types';

const { REACT_APP_CATALOG } = process.env;

const getAllByStatus = async (
  status: EQuoteStatus,
  params: AxiosRequestConfig['params'],
  config?: Pick<AxiosRequestConfig, 'signal'>
) => {
  return apiGet<AxiosRequestConfig, IQuoteListByStatusResponse>(`${REACT_APP_CATALOG}quote-actions/list/${status}`, {
    params,
    ...config
  })
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getCountsByStatus = (params: AxiosRequestConfig['params']) => {
  return apiGet<AxiosRequestConfig, IOutputCountsByStatusResponse>(`${REACT_APP_CATALOG}quote-actions/status-counts`, {
    params
  })
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getFilters = async (status?: EQuoteStatus) => {
  return apiGet<AxiosRequestConfig, IOutputQuoteListFilters>(`${REACT_APP_CATALOG}quote-actions/filters/list`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Trae la data completa de una cotización, incluyendo
 * info general, país, usuario solicitante e items a cotizar.
 */
const getQuoteDetails = async (quoteId: number) => {
  return apiGet<AxiosRequestConfig, IQuoteApiResponse>(`${REACT_APP_CATALOG}quote-actions/details/${quoteId}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/** Genera el PDF de una cotización y devuelve la URL de descarga. */
const generatePdf = async (quoteId: number): Promise<{ url: string }> => {
  return apiPost<AxiosRequestConfig, undefined>(`${REACT_APP_CATALOG}quote-actions/generate-pdf/${quoteId}`)
    .then(response => response.data)
    .catch((error: AxiosError) => Promise.reject(error));
};

const assignResponsible = async (quoteId: number, userId: number) => {
  return apiPost<AxiosRequestConfig, { responsibleUserId: number }>(
    `${REACT_APP_CATALOG}quote-responsible-actions/${quoteId}`,
    {
      responsibleUserId: userId
    }
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/** Obtiene la lista de responsables vigentes de una cotización. */
const getQuoteResponsibles = async (quoteId: number) => {
  return apiGet<AxiosRequestConfig, IQuoteResponsibleUser[]>(
    `${REACT_APP_CATALOG}quote-responsible-actions/${quoteId}/responsibles`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const unassignResponsible = async (id: number) => {
  return apiDelete<AxiosRequestConfig, { responsibleUserId: number }>(
    `${REACT_APP_CATALOG}quote-responsible-actions/${id}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const cancelQuote = async (quoteId: number, cancellationReason: string) => {
  return apiPut<AxiosRequestConfig, { cancellationReason: string }>(
    `${REACT_APP_CATALOG}quote-actions/cancel-quote/${quoteId}`,
    { cancellationReason }
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getPurchaseTracking = async (quoteId: number) => {
  return apiGet<AxiosRequestConfig, IQuotePurchaseTrackingOrder[]>(`${REACT_APP_CATALOG}quote-actions/purchase-tracking/${quoteId}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const QuoteService = {
  getAllByStatus,
  assignResponsible,
  unassignResponsible,
  getQuoteResponsibles,
  getFilters,
  getCountsByStatus,
  getQuoteDetails,
  generatePdf,
  cancelQuote,
  getPurchaseTracking
};
